/**
 * Chat Notification Provider
 * Plays sound and shows popup when new chat messages arrive
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MessageCircle, X, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChatNotificationContext = createContext();

export const useChatNotification = () => useContext(ChatNotificationContext);

// Notification sound as base64 (short notification chime)
const NOTIFICATION_SOUND = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYZEkBoLAAAAAAD/+9DEAAAGAAGn9AAAIuTJs/8MUAB4aW1lQUJTQUJTAAADMzMzMzP/+9DEGQPAAADSAAAAACAAGkAAAAATMzMzMzP/+9DEOAPAAADSAAAAACAAGkAAAAATMzMzM//7EMRFg8AAANIAAAAgAAA0gAAABMz/+xDEXYPAAADSAAAAIAAANIAAAAT/+xDEdYAAAADSAAAAAAAA0gAAAAD/+xDEigAAADSAAAAAAAA0gAAABP/7EMSYAAAA0gAAAAAAAADSAAAAE//sQxKAAAADSAAAAAAAAANIAAAAE//sQxLAAAADSAAAAAAAAANIAAAAA';

export const ChatNotificationProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [lastUnreadCount, setLastUnreadCount] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const audioRef = useRef(null);
  const pollingRef = useRef(null);

  // Check if user is logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
  }, [location]);

  // Initialize audio
  useEffect(() => {
    audioRef.current = new Audio(NOTIFICATION_SOUND);
    audioRef.current.volume = 0.5;
  }, []);

  // Play notification sound
  const playSound = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(e => console.log('Audio play failed:', e));
    }
  }, [soundEnabled]);

  // Show notification popup
  const showNotification = useCallback((message) => {
    const id = Date.now();
    const notification = {
      id,
      visitorName: message.visitor_name || 'New Visitor',
      message: message.last_message || 'New message received',
      conversationId: message.conversation_id,
      timestamp: new Date()
    };

    setNotifications(prev => [...prev, notification]);
    playSound();

    // Auto-dismiss after 10 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 10000);
  }, [playSound]);

  // Dismiss notification
  const dismissNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Go to chat
  const goToChat = (conversationId) => {
    dismissNotification(conversationId);
    navigate('/live-chat');
  };

  // Poll for new messages
  useEffect(() => {
    if (!isLoggedIn) return;

    // Don't poll if already on live-chat page
    const isOnChatPage = location.pathname === '/live-chat';

    const checkForNewMessages = async () => {
      try {
        const response = await fetch(`${API_URL}/api/chat/unread-count`);
        const data = await response.json();
        
        if (data.success) {
          const currentUnread = data.total_unread_messages || 0;
          
          // If unread count increased and not on chat page, show notification
          if (currentUnread > lastUnreadCount && !isOnChatPage) {
            // Fetch latest conversation to show in notification
            const convResponse = await fetch(`${API_URL}/api/chat/conversations?limit=1`);
            const convData = await convResponse.json();
            
            if (convData.success && convData.conversations?.length > 0) {
              const latestConv = convData.conversations[0];
              if (latestConv.unread_count > 0) {
                showNotification({
                  visitor_name: latestConv.visitor_name,
                  last_message: 'New message received',
                  conversation_id: latestConv.conversation_id
                });
              }
            }
          }
          
          setLastUnreadCount(currentUnread);
        }
      } catch (error) {
        console.error('Error checking for new messages:', error);
      }
    };

    // Initial check
    checkForNewMessages();

    // Poll every 5 seconds
    pollingRef.current = setInterval(checkForNewMessages, 5000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [isLoggedIn, location.pathname, lastUnreadCount, showNotification]);

  const value = {
    notifications,
    soundEnabled,
    setSoundEnabled,
    dismissNotification,
    goToChat,
    unreadCount: lastUnreadCount
  };

  return (
    <ChatNotificationContext.Provider value={value}>
      {children}
      
      {/* Notification Popups */}
      <div className="fixed top-4 right-4 z-[9999] space-y-3">
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className="bg-white rounded-xl shadow-2xl border border-gray-200 p-4 w-80 animate-slide-in-right"
            style={{
              animation: 'slideInRight 0.3s ease-out'
            }}
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-[#00AEEF] rounded-full flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <h4 className="font-semibold text-gray-900 text-sm">New Chat Message</h4>
                  <button
                    onClick={() => dismissNotification(notification.id)}
                    className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <X className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
                <p className="text-sm text-gray-600 font-medium">{notification.visitorName}</p>
                <p className="text-xs text-gray-500 truncate">{notification.message}</p>
                <button
                  onClick={() => goToChat(notification.conversationId)}
                  className="mt-3 w-full py-2 bg-[#00AEEF] text-white text-sm font-medium rounded-lg hover:bg-[#0096d6] transition-colors flex items-center justify-center gap-2"
                >
                  Open Chat
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* CSS Animation */}
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in-right {
          animation: slideInRight 0.3s ease-out;
        }
      `}</style>
    </ChatNotificationContext.Provider>
  );
};

export default ChatNotificationProvider;
